// @ts-nocheck
/* eslint-disable camelcase */
import { currencyFormat } from '../../../utils/formatter'
import { calculateAprcFromBands } from '../../helpers/elasticsearch/aprc'
import { effectiveRate, getLoanRate, monthlyPayment, toMonths } from './loans'

export interface CalculatorInputs {
  [key: string]: any
}
export interface ValueSlice {
  valueFromFunction: string
}

export type ESHitObject = any

const roundTo2DP = (value) => Math.round(value * 100) / 100

type CalculatorFunction = (inputs: CalculatorInputs, hit: ESHitObject) => any

const calculatedValueLength = 'calculatedValue.'.length
/** Recursively looks in a contentful schema object for `valueFrom` fields that match
 * the `calculatedValue.something` format so we can generate the calculations later.
 */
export function findCalculatedValuesInSchema(schema: any): string[] {
  let result: string[] = []
  for (const key in schema) {
    if (key === 'valueFrom') {
      const pathString = schema[key]
      if (!pathString) {
        // TODO: Add dev mode warning
      } else {
        const calculatedValueIndex = pathString.search('calculatedValue.')
        if (calculatedValueIndex !== -1) {
          const functionName = pathString.substr(calculatedValueLength)
          result.push(functionName)
        }
      }
    } else if (typeof schema[key] === 'object') {
      // Recursively continue to find values that match the schema
      result = [...result, ...findCalculatedValuesInSchema(schema[key])]
    }
  }
  return result
}

/**
 * Generates the return value for a `valueFromFunction` field based on calculator inputs and ES hit object
 * @param {object} inputs The calculator
 * @param {object} hit The hit object from elastic search
 */
export function generateValueCalculated(
  functionName: string,
  inputs: CalculatorInputs,
  hit: ESHitObject,
  previousResults: any,
) {
  const calcFunction = calculatorFunctions[functionName]
  if (typeof calcFunction !== 'function') {
    // TODO: Do this warning only in dev mode
    // eslint-disable-next-line no-console
    console.warn(`Calculator function ${functionName} doesn't exist.`) // eslint-disable-line no-console
    return null
  }
  return calcFunction(inputs, hit, previousResults)
}

/** Contains a list of calculator functions to be used for generating custom
 * calculated fields.
 * Functions in this take two inputs:
 *  - inputs: object containing all the calculator input fields and their values
 *  - hit: elasticsearch result hit object
 */
export const calculatorFunctions: {
  [key: string]: CalculatorFunction
} = {
  loansApr: (inputs: CalculatorInputs, hit) => {
    const rates = hit._source?.definition?.rates ?? []
    const term = toMonths(inputs.loanTerm)
    const amount = inputs.loanAmount
    const apr = getLoanRate(rates, amount, term)?.apr
    if (!apr) {
      return
    }
    return `${apr.toFixed(1)}% APR`
  },
  loansAprc: (inputs: CalculatorInputs, hit) => {
    const aprc = calculateAprcFromBands(
      [{ numberOfMonths: toMonths(inputs.loanTerm), amount: hit.fields.monthly_repayment[0] }],
      inputs.loanAmount,
      false,
      0,
      parseFloat(hit.fields.fees),
    )

    return `${aprc}% APRC`
  },
  loansTotalPayable: (inputs: CalculatorInputs, hit) => {
    const rates = hit._source?.definition?.rates ?? []
    const term = toMonths(inputs.loanTerm)
    const amount = inputs.loanAmount
    const apr = getLoanRate(rates, amount, term)?.apr
    if (!apr) {
      return
    }
    const effectRate = effectiveRate(apr)
    const monthlyPay = monthlyPayment(amount, term, effectRate)
    return roundTo2DP(monthlyPay * term)
  },

  loansRepText: (inputs: CalculatorInputs, hit) => {
    const rates = hit._source?.definition?.rates ?? []
    const term = toMonths(inputs.loanTerm)
    const amount = inputs.loanAmount
    const rate = getLoanRate(rates, amount, term)
    const apr = rate?.apr
    if (!apr) {
      return
    }
    const fixedRate = rate?.fixed_annual_rate || apr
    const effectRate = effectiveRate(apr)
    const monthlyPay = monthlyPayment(amount, term, effectRate)
    const totalFees = roundTo2DP(
      rate.arrangement_fee_pounds + amount * (rate.arrangement_fee_percent / 100),
    )
    const totalPayment = roundTo2DP(monthlyPay * term + totalFees)
    const noCurrency = false
    const decimalPoints = 2
    const formattedRate = (unformattedRate) =>
      `${new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(unformattedRate)}%`
    return `Assumed borrowing of ${currencyFormat(
      amount,
    )} over ${term} months at a fixed rate of ${formattedRate(
      fixedRate,
    )} per annum would result in a representative rate of ${formattedRate(
      apr,
    )} APR, monthly repayments of ${currencyFormat(
      monthlyPay,
      noCurrency,
      decimalPoints,
      decimalPoints,
    )} ${
      totalFees > 0.0
        ? `plus an arrangement fee of ${currencyFormat(
            totalFees,
            noCurrency,
            decimalPoints,
            decimalPoints,
          )},`
        : ''
    } and a total amount repayable of ${currencyFormat(
      totalPayment,
      noCurrency,
      decimalPoints,
      decimalPoints,
    )}.`
  },
  creditCardRepayment: (inputs: CalculatorInputs, hit: any) => {
    const maximum_credit_limit_pounds_numeric =
      hit?._source?.definition?.maximum_credit_limit_pounds_numeric

    const monthlyPayments = parseInt(inputs.monthlyPayments)
    const transferAmount = parseInt(inputs.transferAmount)

    const { total_months_repayable, interest_and_fees } = hit.fields

    if (
      maximum_credit_limit_pounds_numeric &&
      transferAmount > maximum_credit_limit_pounds_numeric
    ) {
      return 'Transfer is over the card limit'
    }

    if (
      total_months_repayable[0] === -1 ||
      interest_and_fees[0] === -1 ||
      monthlyPayments <
        minimumPayment(
          transferAmount,
          hit?._source?.definition,
          inputs.introRateField,
          inputs.standardRateField,
        )
    ) {
      return 'Monthly payments are too low'
    }

    const interestAndFees =
      interest_and_fees[0] > 0
        ? `fees & interest of £${interest_and_fees[0].toFixed(2)}`
        : 'no fees or interest'
    return `${total_months_repayable} months with ${interestAndFees}`
  },
  loanMonthlyRepayment: (inputs: CalculatorInputs, hit: any) => {
    return (hit.fields.total_repayable_calculated - hit.fields.fees) / toMonths(inputs.loanTerm)
  },
  loanMonthlyRepaymentLabel: (inputs: CalculatorInputs) => {
    return `To pay off balance over ${toMonths(inputs.loanTerm)} months`
  },
  currencyGetsYou: (inputs: CalculatorInputs) => {
    if (inputs.currencyToUse === 'GBP') {
      return `£${inputs.gbpAmount} gets you`
    } else {
      return `${formatCurrency(inputs.currencyPair, parseInt(inputs.otherAmount))} costs you`
    }
  },
  travelMoney: (inputs: CalculatorInputs, hit: any) => {
    const collectionTypes = {
      airport: {
        field: 'order_online_for_airport_collection',
        errorMessage: 'Airport collection is not available',
      },
      collection: {
        field: 'order_online_for_in_store_collection',
        errorMessage: 'Collection is not available',
      },
      delivery: {
        field: 'order_online_for_home_delivery',
        errorMessage: 'Delivery is not available',
      },
    }

    if (!hit._source.definition[collectionTypes[inputs.collectionType].field]) {
      return collectionTypes[inputs.collectionType].errorMessage
    }

    const amountInPounds =
      inputs.currencyToUse === 'GBP' ? inputs.gbpAmount : hit.fields.amount_received[0]

    const exchangeRate =
      hit.fields.exchange_rate > 0
        ? hit.fields.exchange_rate
        : hit._source.definition.standard_rates?.find(
            (r: any) => r.currency_iso_code === inputs.currencyPair,
          )?.standard_rate_tiers[0].tier_exchange_rate_numeric || 0

    const minimumAmount =
      inputs.currencyToUse === 'GBP'
        ? formatCurrency('GBP', hit._source.definition.minimum_online_order_pounds_numeric)
        : formatCurrency(
            inputs.currencyPair,
            hit._source.definition.minimum_online_order_pounds_numeric * exchangeRate,
          )

    const minimumDeliveryAmount =
      inputs.currencyToUse === 'GBP'
        ? formatCurrency(
            'GBP',
            hit._source.definition.minimum_online_order_for_delivery_pounds_numeric,
          )
        : formatCurrency(
            inputs.currencyPair,
            hit._source.definition.minimum_online_order_for_delivery_pounds_numeric * exchangeRate,
          )

    const maximumAmount =
      inputs.currencyToUse === 'GBP'
        ? formatCurrency('GBP', hit._source.definition.maximum_online_order_pounds_numeric)
        : formatCurrency(
            inputs.currencyPair,
            hit._source.definition.maximum_online_order_pounds_numeric * exchangeRate,
          )

    if (
      inputs.collectionType === 'delivery' &&
      amountInPounds < hit._source.definition.minimum_online_order_for_delivery_pounds_numeric
    ) {
      return `${minimumDeliveryAmount} minimum order`
    } else if (amountInPounds < hit._source.definition.minimum_online_order_pounds_numeric) {
      return `${minimumAmount} minimum order`
    } else if (amountInPounds > hit._source.definition.maximum_online_order_pounds_numeric) {
      return `${maximumAmount} maximum order`
    }

    return formatCurrency(
      inputs.currencyToUse === 'GBP' ? inputs.currencyPair : 'GBP',
      parseFloat(hit.fields.amount_received[0]),
    )
  },
}

const minimumPayment = (
  transferAmount: number,
  product: any,
  introField: string,
  standardField: string,
) => {
  const {
    alternative_minimum_payment_percent_numeric,
    higher_minimum_payment_percent_numeric,
    minimum_payment_including_interest_and_fees_percent_numeric,
  } = product

  const fees = calculateFees(transferAmount, product)
  const interest = calculateInterest(transferAmount + fees, product, introField, standardField)

  const lowerPaymentPercent =
    alternative_minimum_payment_percent_numeric ||
    minimum_payment_including_interest_and_fees_percent_numeric

  const standardPayment = (transferAmount + fees + interest) * (lowerPaymentPercent / 100)

  const higherPayment = transferAmount * (higher_minimum_payment_percent_numeric / 100)

  return Math.max(standardPayment, higherPayment)
}

const calculateFees = (amount: number, product: any) => {
  const { card_fee_annual_pounds_numeric = 0, card_fee_monthly_pounds_numeric = 0 } = product

  const feeList = [
    [
      product.balance_transfer_intro_fee_percent_numeric,
      product.balance_transfer_intro_minimum_fee_pounds_numeric,
    ],
    [
      product.balance_transfer_standard_fee_percent_numeric,
      product.balance_transfer_standard_minimum_fee_pounds_numeric,
    ],
    [
      product.money_transfer_intro_fee_percent_numeric,
      product.money_transfer_intro_minimum_fee_pounds_numeric,
    ],
    [
      product.money_transfer_standard_fee_percent_numeric,
      product.money_transfer_standard_minimum_fee_percent_numeric,
    ],
  ]

  return (
    feeList.reduce((a, [feePercent, minimumFee]) => {
      if (a > 0 || !feePercent) return a

      const balance_transfer_fee = amount * (feePercent / 100)

      if (balance_transfer_fee > minimumFee) {
        return a + balance_transfer_fee
      } else {
        return a + minimumFee
      }
    }, 0) + (card_fee_annual_pounds_numeric || card_fee_monthly_pounds_numeric)
  )
}

const calculateInterest = (
  amount: number,
  product: any,
  introField: string,
  standardField: string,
) => {
  const rate = product[introField] || product[standardField] || 0
  return amount * (rate / 100)
}

const formatCurrency = (currency: string, amount: number) => {
  const prefixCurrencies = {
    GBP: '£',
    USD: '$',
    MXN: '$',
    MYR: 'RM',
    HKD: '$',
    NZD: '$',
    BRL: 'R$',
    XCD: '$',
    JPY: '¥',
    BBD: '$',
    AUD: '$',
    CAD: '$',
    ZAR: 'R',
    THB: '฿',
    TRY: '₺',
  }
  const suffixCurrencies = {
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    HRK: 'kn',
    ISK: 'kr',
    AED: 'د.إ',
  }

  const roundedAmount = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(amount)
    .replace(/[.,]00$/, '')

  return prefixCurrencies[currency]
    ? `${prefixCurrencies[currency]}${roundedAmount}`
    : `${roundedAmount} ${suffixCurrencies[currency] || currency}`
}
